import { useCallback, useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { AlertValue, alertOpen } from "../../features/alert.slice";
import { ConstMessage } from "../../config/const";
import { auth } from "../../firebase";
import { Spiner } from "../../components/spinner/Spiner";
import { Progress } from "../../components/progress/Progress";
import Button from "../../components/button/Button";
import TableList, { GridCol, GridRow } from "../../components/table/TableList";
import { PointAdjustmentListExportModal } from "./PointAdjustmentListExportModal";
import companiesQuery from "../../firestore/Companies.query";
import { Company } from "../../model/Companies";
import pointDataQuery from '../../firestore/PointData.query';
import { PointAdjustHistory, PtCategorySettings, getExpirationDate } from "../../model/PointData";
import { convertDateToString, convertDateSecToStringCompactFormat } from "../../utility/ConvertUtily";
import { checkOption } from "../../controller/OptionController";
import { requestGetPointAdjustHistory } from "../../api/Point.api";

export const PointAdjustmentHistory = () => {

  const dispatch = useDispatch();
  const navigation = useNavigate();
  // ログイン中の団体管理者情報
  const currentUser = auth.currentUser;

  const [isLoad, setIsLoad] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [company, setCompany] = useState<Company>();
  const [pointAdjustmentList, setPointAdjustmentList] = useState<PointAdjustHistory[]>([]);
  const [selectNumber, setSelectNumber] = useState(50);
  const [firstPageFlg, setFirstPageFlg] = useState(false);
  const [loadProgress, setLoadProgress] = useState<number>(-1);
  const [nowPage, setNowPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [pointCategorySettingMap, setPointCategorySettingMap] = useState<Record<string, PtCategorySettings>>({});

  useEffect(() => {
    const f = async () => {
      try {
        // 団体を取得
        const company = await companiesQuery.getCompany(currentUser?.tenantId!);
        setCompany(company);
        // ポイントカテゴリ設定一覧の取得
        const pointCategorySettingList = await pointDataQuery.getPtCategorySettingsList(currentUser?.tenantId!);
        const map: Record<string, PtCategorySettings> = {};
        pointCategorySettingList.forEach((data) => {
          const expiryMap = data.dateOfExpiryMap;
          if (expiryMap?.expirationYm) {
            if (typeof (expiryMap.expirationYm as any)?.toDate === "function") {
              expiryMap.expirationYm = (expiryMap.expirationYm as any).toDate();
            }
          }
          map[data.categoryId] = data;
        });
        setPointCategorySettingMap(map);
      } catch (err) {
        console.error("ポイントカテゴリ設定取得失敗:", err);
      }
    };
    f();
  }, [currentUser?.tenantId]);


  // 調整履歴一覧を取得する共通処理
  useEffect(() => {
    const fetchPointsByPage = async () => {
      try {
        const result = await requestGetPointAdjustHistory(nowPage, selectNumber);
        if (result.success) {
          const mappedList = result.pointAdjustHistory.map((item) => {
            // カテゴリ設定がある場合だけ有効期限計算
            const categorySetting = pointCategorySettingMap[item.ptCategoryId];
            if (item.grantPoints && item.grantPoints > 0 && categorySetting) {
              item.expirationDate = getExpirationDate(categorySetting, item.occurrenceDate);
            } else {
              item.expirationDate = undefined;
            }
            return item;
          });
          setPointAdjustmentList(mappedList);
          setTotalCount(result.totalCount);
        } else {
          console.error("調整履歴取得失敗:", result.error);
          dispatch(alertOpen(AlertValue(ConstMessage.ERR_FAILED_GET_DATA, "danger")));
        }
      } catch (err) {
        console.error("調整履歴取得失敗:", err);
        dispatch(alertOpen(AlertValue(ConstMessage.ERR_FAILED_GET_DATA, "danger")));
      } finally {
        setIsLoad(false);
      }
    };
      fetchPointsByPage();
  }, [nowPage, selectNumber, pointCategorySettingMap, dispatch]);

  // 表示件数変更時に1ページ目を表示する
  useEffect(() => {
    setFirstPageFlg(true)
  },[selectNumber]);

  // ページング処理
  const pagingFunc = useCallback((page: number) => {
    setFirstPageFlg(false);
    setNowPage(page);
  }, []);

  // 表示件数セレクトボックス
  const selectNumberList = [
    { id: 50, name: "50" },
    { id: 100, name: "100" },
    { id: 200, name: "200" },
  ];

  // 調整履歴の項目
  const columns: GridCol[] = [
    {
      field: "createAt",
      headerName: "登録日",
      style: "",
      sort: false
    },
    {
      field: "ptCategoryName",
      headerName: "カテゴリ",
      style: "w-2/12",
      sort: false
    },
    {
      field: "ptTypeName",
      headerName: "タイプ",
      style: "w-2/12",
      sort: false
    },
    {
      field: "ptName",
      headerName: "ポイント名",
      style: "",
      sort: false
    },
    {
      field: "expirationDate",
      headerName: "有効期限",
      style: "",
      sort: false
    },
    {
      field: "occurrenceDate",
      headerName: "ポイント発生日",
      style: "",
      sort: false
    },
    {
      field: "grantPoints",
      headerName: "ポイント数",
      style: "",
      sort: false
    },
    {
      field: "info",
      headerName: "",
      style: "",
      sort: false
    },
  ];

  // 調整履歴の一覧データ
  const rowList: GridRow[] = pointAdjustmentList.map<GridRow>((item) => {
    return{
      rowData: {
        createAt: convertDateToString(item.createAt),
        ptCategoryName: item.ptCategoryName,
        ptTypeName: item.ptTypeName,
        ptName: item.ptName,
        expirationDate: item.expirationDate? convertDateToString(item.expirationDate) : "",
        occurrenceDate: convertDateToString(item.occurrenceDate),
        grantPoints: (<div className={"text-right 2xl:mr-16"}>{item.grantPoints}</div>),
        info: (<Button className={"px-4 whitespace-nowrap"} onClick={()=> navigation(`${item.id}/info`)}>詳細</Button>),
      },
      sortableRowData: {
        createAt: convertDateSecToStringCompactFormat(item.createAt),
      },
      style: 'sm:whitespace-normal'
    };
  });

  return(
    <div className="h-full">
      <div className="flex justify-between items-center px-6 pt-6 pb-4">
        <h2 className="text-xl font-semibold text-gray-900">
          ポイント調整履歴
        </h2>
        <div className="flex px-1">
          <Button
            className="h-8 w-30 items-center text-sm"
            size={"md"}
            onClick={async () => {
              if (await checkOption("point", dispatch)) {
                setIsDialogOpen(!isDialogOpen);
                }
              }
            }
            children={"履歴出力"}
          />
        </div>
      </div>
      <Progress
        subText={ConstMessage.PROGRESS_SUB_TEXT}
        open={loadProgress >= 0 && loadProgress < 100}
        progressNum={loadProgress}
        color={"green"}
      />
      {isLoad === true ? <Spiner/> : (
        <>
          <div>
            {isDialogOpen && (
              <PointAdjustmentListExportModal
                children
                open={isDialogOpen}
                toggle={() => {setIsDialogOpen(!isDialogOpen)}}
                closeOnClickOutside={false}
                pointAdjustmentList={pointAdjustmentList}
                setLoadProgress={setLoadProgress}
                company={company}
                pointCategorySettingMap={pointCategorySettingMap}
              />
            )}
            <div className={"px-6 pb-2"}>
              <TableList
                header
                gridCol={columns}
                gridRow={rowList}
                gridRowLength={totalCount}
                setSelectNumber={setSelectNumber}
                selectNumberList={selectNumberList}
                limit={selectNumber}
                setFirstPageFlg={setFirstPageFlg}
                firstPageFlg={firstPageFlg}
                minWidth="min-w-[990px]"
                defaultSortKey={columns[0].field}
                defaultSortOrder={"desc"}
                pagingFunc={pagingFunc}
              />
            </div>
          </div>
        </>
      )}
    </div>
  );
};